import React from 'react';

interface AuditLogDetailsProps {
  action: string;
  metadata?: any;
}

export const AuditLogDetails: React.FC<AuditLogDetailsProps> = ({ action, metadata }) => {
  if (!metadata) {
    return <span className="text-gray-500 dark:text-gray-400">Completed "{action.toLowerCase().replace(/_/g, ' ')}"</span>;
  }

  let parsedMetadata = metadata;
  if (typeof metadata === 'string') {
    try {
      parsedMetadata = JSON.parse(metadata);
    } catch {
      parsedMetadata = metadata;
    }
  }

  if (typeof parsedMetadata === 'string') {
    return <span className="text-gray-600 dark:text-gray-300 font-mono text-xs">{parsedMetadata}</span>;
  }

  if (typeof parsedMetadata === 'object' && parsedMetadata !== null) {
    // 1. Specialized formatting for MODULES_UPDATED or modules object
    if (action === 'MODULES_UPDATED' || parsedMetadata.modules || parsedMetadata.enabled) {
      const enabledList: string[] = [];
      const disabledList: string[] = [];

      if (parsedMetadata.modules && typeof parsedMetadata.modules === 'object') {
        Object.entries(parsedMetadata.modules).forEach(([k, v]) => {
          if (v === true) enabledList.push(k);
          else if (v === false) disabledList.push(k);
        });
      }
      if (Array.isArray(parsedMetadata.enabled)) {
        enabledList.push(...parsedMetadata.enabled);
      }
      if (Array.isArray(parsedMetadata.disabled)) {
        disabledList.push(...parsedMetadata.disabled);
      }

      if (enabledList.length > 0 || disabledList.length > 0) {
        return (
          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
            {enabledList.slice(0, 4).map((mod) => (
              <span
                key={mod}
                className="px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-950/80 text-emerald-800 dark:text-emerald-300 font-mono text-[10px] font-bold border border-emerald-300 dark:border-emerald-800"
              >
                +{mod}
              </span>
            ))}
            {disabledList.slice(0, 3).map((mod) => (
              <span
                key={mod}
                className="px-1.5 py-0.5 bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-gray-400 font-mono text-[10px] border border-gray-300 dark:border-slate-700 line-through"
              >
                -{mod}
              </span>
            ))}
            {(enabledList.length > 4 || disabledList.length > 3) && (
              <span className="text-[10px] font-mono text-gray-500 font-bold">
                +{enabledList.length + disabledList.length - 7} more
              </span>
            )}
          </div>
        );
      }
    }

    // 2. Role assignment / changes
    if (parsedMetadata.role || action.includes('ROLE')) {
      return (
        <span className="text-gray-800 dark:text-gray-200 text-xs">
          Role set to <strong className="font-bold text-[#0044FF] dark:text-[#FFD600] font-mono">{parsedMetadata.role}</strong>
          {parsedMetadata.name && <span className="ml-1 text-gray-600 dark:text-gray-400">for <strong>{parsedMetadata.name}</strong></span>}
        </span>
      );
    }

    // 3. Organization Title / Node name
    if (parsedMetadata.title || (parsedMetadata.name && action.includes('NODE'))) {
      return (
        <span className="text-gray-800 dark:text-gray-200 text-xs">
          Title: <strong className="font-bold text-black dark:text-white">{parsedMetadata.title || parsedMetadata.name}</strong>
        </span>
      );
    }

    // 4. Field change
    if (parsedMetadata.field && parsedMetadata.changedTo !== undefined) {
      return (
        <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
          <strong>{parsedMetadata.field}</strong> → <span className="text-[#0044FF] dark:text-[#FFD600] font-bold">{String(parsedMetadata.changedTo)}</span>
        </span>
      );
    }

    // 5. Credits
    if (parsedMetadata.creditsAdded) {
      return (
        <span className="text-emerald-700 dark:text-emerald-400 font-bold font-mono text-xs">
          +{parsedMetadata.creditsAdded} credits
        </span>
      );
    }

    // 6. Generic keys formatting
    const keys = Object.keys(parsedMetadata).filter(
      (k) => !['ipAddress', 'timestamp', 'actor', 'id'].includes(k)
    );

    if (keys.length === 0) {
      return <span className="text-gray-600 dark:text-gray-300 text-xs">Action: {action.replace(/_/g, ' ')}</span>;
    }

    return (
      <div className="flex flex-wrap gap-1.5 items-center mt-0.5 text-xs">
        {keys.map((key) => {
          const val = parsedMetadata[key];
          let valStr = '';
          if (typeof val === 'object' && val !== null) {
            valStr = Object.entries(val)
              .map(([k, v]) => `${k}:${v}`)
              .join(', ');
          } else {
            valStr = String(val);
          }

          if (valStr.length > 60) {
            valStr = valStr.substring(0, 57) + '...';
          }

          return (
            <span
              key={key}
              className="inline-flex items-center px-1.5 py-0.5 bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-gray-300 font-mono text-[10px] border border-gray-200 dark:border-slate-700"
            >
              <strong className="font-semibold text-gray-500 dark:text-gray-400 mr-1 uppercase">{key}:</strong> {valStr}
            </span>
          );
        })}
      </div>
    );
  }

  return <span className="text-gray-500 dark:text-gray-400 text-xs">Completed "{action.toLowerCase().replace(/_/g, ' ')}"</span>;
};
